import { Table, TableBody, TableHead, TableRow, Skeleton, Typography } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  CustomButton,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  NoDataSkeleton,
} from '../../common/components';
import { INSTALLMENT_COLUMNS } from '../constants/anniversaryPlanConstants';

const formatValue = (key, value, isVirtual = false) => {
  if (value === null || value === undefined) return '-';
  if (key === 'payment_total' || key === 'total_amount') {
    if (typeof value !== 'number') return value;
    const formatted = `$${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return isVirtual ? `~${formatted}` : formatted;
  }
  if (key.includes('date') || key.includes('due')) {
    if (!value) return '-';
    const str = String(value).split('T')[0];
    return isVirtual ? `~${str}` : str;
  }
  return value;
};

const estimateNextAmount = (amountHistory) => {
  const last4 = amountHistory.slice(-4);
  if (last4.length < 2) return last4[last4.length - 1] ?? 0;
  const diffs = [];
  for (let j = 1; j < last4.length; j++) {
    diffs.push(last4[j] - last4[j - 1]);
  }
  const avgDiff = diffs.reduce((a, b) => a + b, 0) / diffs.length;
  return last4[last4.length - 1] + avgDiff;
};

const generateVirtualRows = (rows, agreementInstallments) => {
  if (!rows.length || !agreementInstallments) return [];
  const totalGenerated = rows.length;
  const remaining = agreementInstallments - totalGenerated;
  if (remaining <= 0) return [];

  const lastRow = rows[rows.length - 1];
  const lastThirdDueStr = lastRow.third_due_date;
  if (!lastThirdDueStr) return [];

  const datePart = String(lastThirdDueStr).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const amountHistory = rows
    .map((r) => (typeof r.total_amount === 'number' ? r.total_amount : null))
    .filter((v) => v !== null);

  const virtual = [];
  for (let i = 1; i <= remaining; i++) {
    const totalMonths = (month - 1) + i;
    const y = year + Math.floor(totalMonths / 12);
    const m = totalMonths % 12;
    const projectedDate = new Date(y, m, day);
    if (projectedDate >= today) break;

    const estimatedAmount = estimateNextAmount(amountHistory);
    amountHistory.push(estimatedAmount);

    virtual.push({
      fee_number1: totalGenerated + i,
      first_due_date: null,
      second_due_date: null,
      third_due_date: projectedDate.toISOString().split('T')[0],
      payment_date: null,
      payment_total: null,
      total_amount: estimatedAmount,
      _virtual: true,
    });
  }
  return virtual;
};

const AnniversaryPlanInstallmentsDialog = ({
  open,
  onClose,
  contractNumber,
  agreementInstallments,
  installmentsData,
  isLoading,
}) => {
  const rows = installmentsData?.results ?? [];
  const virtualRows = generateVirtualRows(rows, agreementInstallments);
  const allRows = [...rows, ...virtualRows];

  return (
    <DialogContainer open={open} onClose={onClose} size="large">
      <DialogTitle>Cuotas — Contrato {contractNumber}</DialogTitle>
      <DialogBody>
        {isLoading ? (
          <>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 0.5 }} />
            ))}
          </>
        ) : allRows.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No se encontraron cuotas
          </Typography>
        ) : (
          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {INSTALLMENT_COLUMNS.map((col) => (
                    <HeadTableCell key={col.key}>{col.label}</HeadTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {allRows.length === 0 ? (
                  <NoDataSkeleton colSpan={INSTALLMENT_COLUMNS.length} />
                ) : (
                  allRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={row._virtual ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
                    >
                      {INSTALLMENT_COLUMNS.map((col) => (
                        <RowTableCell
                          key={col.key}
                          sx={row._virtual ? { color: 'text.secondary', fontStyle: 'italic' } : undefined}
                        >
                          {col.key === 'fee_number1' && row._virtual
                            ? `~${row[col.key]}`
                            : formatValue(col.key, row[col.key], row._virtual)}
                        </RowTableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        )}
      </DialogBody>
      <CustomButton
        variant="outlined"
        onClick={onClose}
        sx={{ m: 2, alignSelf: 'flex-end' }}
      >
        Cerrar
      </CustomButton>
    </DialogContainer>
  );
};

export default AnniversaryPlanInstallmentsDialog;
